import React, { useState } from 'react';
import { Settings, Sparkles, Trash2, Copy, ChevronUp, ChevronDown, Palette } from 'lucide-react';
import { EMAIL_BLOCK_REGISTRY } from './emailBlockRegistry';

const LABEL = "block text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1";
const INPUT = "w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white";
const SELECT = INPUT;

const COLUMN_RATIOS = {
  2: [{ value: '50-50', label: '50 / 50' }, { value: '33-67', label: '33 / 67' }, { value: '67-33', label: '67 / 33' }],
  3: [{ value: '33-33-33', label: '33 / 33 / 33' }, { value: '25-50-25', label: '25 / 50 / 25' }, { value: '50-25-25', label: '50 / 25 / 25' }],
};

const SOCIAL_PLATFORMS = ['linkedin', 'twitter', 'facebook', 'instagram', 'youtube', 'website', 'tiktok'];

/**
 * Right panel — context-sensitive property editor for the selected block.
 */
export default function EmailBlockPropsPanel({ block, onChange, onDelete, onDuplicate, onMoveUp, onMoveDown, showAIPanel, onToggleAI }) {
  if (!block) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <Settings className="w-6 h-6 text-stone-300 mx-auto mb-2" />
          <p className="text-xs text-stone-400">Select a block to edit its properties</p>
        </div>
      </div>
    );
  }

  const def = EMAIL_BLOCK_REGISTRY[block.block_type];
  const props = block.props || {};
  const update = (key, val) => onChange({ ...props, [key]: val });

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Block header */}
      <div className="px-3 py-2.5 border-b border-stone-200 flex items-center justify-between bg-stone-50/50">
        <div>
          <p className="text-xs font-semibold text-stone-700">{def?.label || block.block_type}</p>
          <p className="text-[10px] text-stone-400">{def?.description}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} className="p-1 text-stone-400 hover:text-stone-700 rounded" title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={onMoveDown} className="p-1 text-stone-400 hover:text-stone-700 rounded" title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button>
          <button onClick={onDuplicate} className="p-1 text-stone-400 hover:text-stone-700 rounded" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
          <button onClick={onDelete} className="p-1 text-red-400 hover:text-red-600 rounded" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* AI toggle */}
      <div className="px-3 py-2 border-b border-stone-100">
        <button onClick={onToggleAI}
          className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${showAIPanel ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
          <Sparkles className="w-3.5 h-3.5" /> AI Agent
        </button>
      </div>

      {/* Block-specific properties */}
      <div className="p-3 space-y-3">
        {block.block_type === 'text' && <TextProps props={props} update={update} />}
        {block.block_type === 'image' && <ImageProps props={props} update={update} />}
        {block.block_type === 'button' && <ButtonProps props={props} update={update} />}
        {block.block_type === 'divider' && <DividerProps props={props} update={update} />}
        {block.block_type === 'spacer' && <SpacerProps props={props} update={update} />}
        {block.block_type === 'columns' && <ColumnsProps props={props} update={update} onChange={onChange} />}
        {block.block_type === 'social' && <SocialProps props={props} update={update} onChange={onChange} />}
        {block.block_type === 'header' && <HeaderProps props={props} update={update} />}
        {block.block_type === 'footer' && <FooterProps props={props} update={update} />}
        {block.block_type === 'signature' && <SignatureProps props={props} update={update} />}

        {/* Common: padding */}
        {props.padding !== undefined && (
          <div>
            <label className={LABEL}>Padding</label>
            <input value={props.padding || ''} onChange={e => update('padding', e.target.value)} className={INPUT} placeholder="16px 24px" />
          </div>
        )}
      </div>
    </div>
  );
}

function TextProps({ props, update }) {
  return (
    <>
      <ColorField label="Text Color" value={props.text_color} onChange={v => update('text_color', v)} />
      <ColorField label="Background" value={props.background_color} onChange={v => update('background_color', v)} />
      <div>
        <label className={LABEL}>Font Size</label>
        <select value={props.font_size || '14px'} onChange={e => update('font_size', e.target.value)} className={SELECT}>
          {['11px', '12px', '13px', '14px', '15px', '16px', '18px', '20px', '24px'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Line Height</label>
        <select value={props.line_height || '1.6'} onChange={e => update('line_height', e.target.value)} className={SELECT}>
          {['1.2', '1.4', '1.5', '1.6', '1.8', '2.0'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </>
  );
}

function ImageProps({ props, update }) {
  return (
    <>
      <div>
        <label className={LABEL}>Image URL</label>
        <input value={props.image_url || ''} onChange={e => update('image_url', e.target.value)} className={INPUT} placeholder="https://..." />
      </div>
      <div>
        <label className={LABEL}>Alt Text</label>
        <input value={props.alt_text || ''} onChange={e => update('alt_text', e.target.value)} className={INPUT} placeholder="Image description" />
      </div>
      <div>
        <label className={LABEL}>Link URL</label>
        <input value={props.link_url || ''} onChange={e => update('link_url', e.target.value)} className={INPUT} placeholder="https://..." />
      </div>
      <div>
        <label className={LABEL}>Caption</label>
        <input value={props.caption || ''} onChange={e => update('caption', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className={LABEL}>Width</label>
        <input value={props.width || '100%'} onChange={e => update('width', e.target.value)} className={INPUT} placeholder="100% or 300px" />
      </div>
      <AlignmentField value={props.alignment} onChange={v => update('alignment', v)} />
      <div>
        <label className={LABEL}>Border Radius</label>
        <select value={props.border_radius || '0px'} onChange={e => update('border_radius', e.target.value)} className={SELECT}>
          {['0px', '4px', '8px', '12px', '16px', '50%'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </>
  );
}

function ButtonProps({ props, update }) {
  return (
    <>
      <div>
        <label className={LABEL}>Button Text</label>
        <input value={props.text || ''} onChange={e => update('text', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className={LABEL}>Link URL</label>
        <input value={props.href || ''} onChange={e => update('href', e.target.value)} className={INPUT} placeholder="https://..." />
      </div>
      <ColorField label="Button Color" value={props.bg_color} onChange={v => update('bg_color', v)} />
      <ColorField label="Text Color" value={props.text_color} onChange={v => update('text_color', v)} />
      <AlignmentField value={props.alignment} onChange={v => update('alignment', v)} />
      <div>
        <label className={LABEL}>Border Radius</label>
        <select value={props.border_radius || '6px'} onChange={e => update('border_radius', e.target.value)} className={SELECT}>
          {['0px', '4px', '6px', '8px', '12px', '24px', '9999px'].map(s => <option key={s} value={s}>{s === '9999px' ? 'Pill' : s}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Width</label>
        <select value={props.width || 'auto'} onChange={e => update('width', e.target.value)} className={SELECT}>
          <option value="auto">Auto</option>
          <option value="full">Full Width</option>
          <option value="200px">200px</option>
          <option value="300px">300px</option>
        </select>
      </div>
      <div>
        <label className={LABEL}>Font Size</label>
        <select value={props.font_size || '14px'} onChange={e => update('font_size', e.target.value)} className={SELECT}>
          {['12px', '14px', '16px', '18px'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </>
  );
}

function DividerProps({ props, update }) {
  return (
    <>
      <ColorField label="Color" value={props.color} onChange={v => update('color', v)} />
      <div>
        <label className={LABEL}>Thickness</label>
        <select value={props.thickness || '1px'} onChange={e => update('thickness', e.target.value)} className={SELECT}>
          {['1px', '2px', '3px', '4px'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Width</label>
        <select value={props.width || '100%'} onChange={e => update('width', e.target.value)} className={SELECT}>
          {['100%', '80%', '60%', '40%'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </>
  );
}

function SpacerProps({ props, update }) {
  return (
    <div>
      <label className={LABEL}>Height</label>
      <select value={props.height || '24px'} onChange={e => update('height', e.target.value)} className={SELECT}>
        {['8px', '16px', '24px', '32px', '40px', '48px', '64px', '80px'].map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

function ColumnsProps({ props, update, onChange }) {
  const count = props.column_count || 2;
  return (
    <>
      <div>
        <label className={LABEL}>Columns</label>
        <select value={count} onChange={e => {
          const n = parseInt(e.target.value);
          const cols = props.columns ? [...props.columns] : [];
          while (cols.length < n) cols.push({ blocks: [{ block_id: 'blk_' + Math.random().toString(36).slice(2, 10), block_type: 'text', props: { content: '<p></p>', padding: '8px', text_color: '#1c1917', background_color: 'transparent', font_size: '14px', line_height: '1.6' } }] });
          const ratio = n === 3 ? '33-33-33' : '50-50';
          onChange({ ...props, column_count: n, column_ratio: ratio, columns: cols.slice(0, n) });
        }} className={SELECT}>
          <option value={2}>2 Columns</option>
          <option value={3}>3 Columns</option>
        </select>
      </div>
      <div>
        <label className={LABEL}>Ratio</label>
        <select value={props.column_ratio || '50-50'} onChange={e => update('column_ratio', e.target.value)} className={SELECT}>
          {(COLUMN_RATIOS[count] || []).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div>
        <label className={LABEL}>Gap</label>
        <select value={props.gap || '16px'} onChange={e => update('gap', e.target.value)} className={SELECT}>
          {['0px', '8px', '12px', '16px', '24px'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </>
  );
}

function SocialProps({ props, update, onChange }) {
  const icons = props.icons || [];
  return (
    <>
      <div>
        <label className={LABEL}>Icon Style</label>
        <select value={props.icon_style || 'color'} onChange={e => update('icon_style', e.target.value)} className={SELECT}>
          <option value="color">Brand Colors</option>
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
      </div>
      <div>
        <label className={LABEL}>Icon Size</label>
        <select value={props.icon_size || '24px'} onChange={e => update('icon_size', e.target.value)} className={SELECT}>
          {['16px', '20px', '24px', '32px'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <AlignmentField value={props.alignment} onChange={v => update('alignment', v)} />
      <div>
        <label className={LABEL}>Links</label>
        <div className="space-y-2">
          {icons.map((icon, i) => (
            <div key={i} className="flex items-center gap-1">
              <select value={icon.platform} onChange={e => {
                const newIcons = [...icons]; newIcons[i] = { ...icon, platform: e.target.value }; onChange({ ...props, icons: newIcons });
              }} className="w-20 px-1.5 py-1 text-[10px] border border-stone-200 rounded bg-white">
                {SOCIAL_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input value={icon.url || ''} onChange={e => {
                const newIcons = [...icons]; newIcons[i] = { ...icon, url: e.target.value }; onChange({ ...props, icons: newIcons });
              }} className="flex-1 px-2 py-1 text-[10px] border border-stone-200 rounded bg-white" placeholder="URL" />
              <button onClick={() => {
                const newIcons = icons.filter((_, idx) => idx !== i); onChange({ ...props, icons: newIcons });
              }} className="text-red-400 hover:text-red-600 text-xs px-1">x</button>
            </div>
          ))}
          <button onClick={() => onChange({ ...props, icons: [...icons, { platform: 'linkedin', url: '' }] })}
            className="text-xs text-stone-500 hover:text-stone-700 underline">+ Add link</button>
        </div>
      </div>
    </>
  );
}

function HeaderProps({ props, update }) {
  return (
    <>
      <div>
        <label className={LABEL}>Logo URL</label>
        <input value={props.logo_url || ''} onChange={e => update('logo_url', e.target.value)} className={INPUT} placeholder="https://..." />
      </div>
      <div>
        <label className={LABEL}>Logo Width</label>
        <input value={props.logo_width || '150px'} onChange={e => update('logo_width', e.target.value)} className={INPUT} />
      </div>
      <AlignmentField label="Logo Alignment" value={props.logo_alignment} onChange={v => update('logo_alignment', v)} />
      <div>
        <label className={LABEL}>Preheader Text</label>
        <input value={props.preheader_text || ''} onChange={e => update('preheader_text', e.target.value)} className={INPUT} placeholder="Preview text in inbox..." />
      </div>
      <ColorField label="Background" value={props.background_color} onChange={v => update('background_color', v)} />
    </>
  );
}

function FooterProps({ props, update }) {
  return (
    <>
      <div>
        <label className={LABEL}>Company Name</label>
        <input value={props.company_name || ''} onChange={e => update('company_name', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className={LABEL}>Address</label>
        <input value={props.address || ''} onChange={e => update('address', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className={LABEL}>Unsubscribe Text</label>
        <input value={props.unsubscribe_text || ''} onChange={e => update('unsubscribe_text', e.target.value)} className={INPUT} />
      </div>
      <div>
        <label className={LABEL}>Unsubscribe URL</label>
        <input value={props.unsubscribe_url || ''} onChange={e => update('unsubscribe_url', e.target.value)} className={INPUT} placeholder="{{UnsubscribeUrl}}" />
      </div>
      <ColorField label="Text Color" value={props.text_color} onChange={v => update('text_color', v)} />
      <ColorField label="Background" value={props.background_color} onChange={v => update('background_color', v)} />
    </>
  );
}

function SignatureProps({ props, update }) {
  return (
    <div>
      <p className="text-xs text-stone-500">Use the "Choose Signature" button on the block to select a signature.</p>
    </div>
  );
}

// Shared field components
function ColorField({ label, value, onChange }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || '#1c1917'} onChange={e => onChange(e.target.value)}
          className="w-7 h-7 rounded border border-stone-200 cursor-pointer" />
        <input value={value || ''} onChange={e => onChange(e.target.value)}
          className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg bg-white" placeholder="#000000 or transparent" />
      </div>
    </div>
  );
}

function AlignmentField({ label = 'Alignment', value, onChange }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <div className="flex gap-1">
        {['left', 'center', 'right'].map(a => (
          <button key={a} onClick={() => onChange(a)}
            className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded-lg border transition-colors capitalize ${value === a ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}>
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}
